import React from 'react';

/**
 * 📦 نظام Cache ذكي للبيانات
 * 
 * - يحفظ البيانات في localStorage
 * - يعرض البيانات المخزنة فوراً
 * - يحدّث البيانات في الخلفية
 * - يظهر "Loading" فقط في المرة الأولى
 */

interface CacheOptions {
  key: string;
  ttl?: number; // Time to live in milliseconds (default: 5 minutes)
}

interface CachedData<T> {
  data: T;
  timestamp: number;
  version: string;
}

const CACHE_VERSION = '1.0';

/**
 * حفظ البيانات في الـ cache
 */
export function setCacheData<T>(key: string, data: T): void {
  try {
    const cacheItem: CachedData<T> = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };
    localStorage.setItem(`cache_${key}`, JSON.stringify(cacheItem));
  } catch (error) {
    console.warn('⚠️ Failed to cache data:', error);
  }
}

/**
 * قراءة البيانات من الـ cache
 */
export function getCacheData<T>(key: string, ttl: number = 5 * 60 * 1000): T | null {
  try {
    const cached = localStorage.getItem(`cache_${key}`);
    if (!cached) return null;

    const cacheItem: CachedData<T> = JSON.parse(cached);
    
    // التحقق من الإصدار
    if (cacheItem.version !== CACHE_VERSION) {
      localStorage.removeItem(`cache_${key}`);
      return null;
    }

    // التحقق من صلاحية البيانات
    const age = Date.now() - cacheItem.timestamp;
    if (age > ttl) {
      // البيانات قديمة، لكن سنعرضها ونحدثها في الخلفية
      console.log(`📦 Cache expired for ${key}, but showing old data...`);
    }

    return cacheItem.data;
  } catch (error) {
    console.warn('⚠️ Failed to read cache:', error);
    return null;
  }
}

/**
 * مسح البيانات من الـ cache
 */
export function clearCacheData(key: string): void {
  try {
    localStorage.removeItem(`cache_${key}`);
  } catch (error) {
    console.warn('⚠️ Failed to clear cache:', error);
  }
}

/**
 * مسح جميع الـ cache
 */
export function clearAllCache(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('cache_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('✅ All cache cleared');
  } catch (error) {
    console.warn('⚠️ Failed to clear all cache:', error);
  }
}

/**
 * Hook مخصص لاستخدام الـ cache مع التحديث في الخلفية
 */
export function useCachedData<T>(
  key: string,
  fetchFunction: () => Promise<T>,
  options: { ttl?: number; deps?: any[] } = {}
): {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);

  const ttl = options.ttl || 5 * 60 * 1000; // 5 minutes default

  const loadData = async (showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      const freshData = await fetchFunction();
      setData(freshData);
      setCacheData(key, freshData);
      setError(null);
    } catch (err: any) {
      console.error(`❌ Error loading ${key}:`, err);
      setError(err.message || 'حدث خطأ أثناء تحميل البيانات');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  React.useEffect(() => {
    // محاولة قراءة البيانات من الـ cache أولاً
    const cachedData = getCacheData<T>(key, ttl);
    
    if (cachedData) {
      console.log(`✅ Loaded ${key} from cache`);
      setData(cachedData);
      setLoading(false);
      setIsFirstLoad(false);
      
      // تحديث البيانات في الخلفية
      loadData(false);
    } else {
      console.log(`📥 Loading ${key} for the first time...`);
      loadData(true);
      setIsFirstLoad(false);
    }
  }, options.deps || []);

  const refresh = async () => {
    await loadData(true);
  };

  return { data, loading, error, refresh };
}
